import { FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, BookMarked, MapPin, Shirt } from "lucide-react";
import { ApiError, api } from "@/lib/api";
import { useAuth, type User, type Workspace } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Wordmark } from "@/components/brand/Wordmark";

type UseCase =
  | "solo_stylist"
  | "studio_team"
  | "celebrity_wardrobe_manager"
  | "archive_consultant"
  | "other";

const USE_CASES: { value: UseCase; label: string; tag: string }[] = [
  { value: "solo_stylist", label: "Solo stylist", tag: "1–2 clients" },
  { value: "studio_team", label: "Studio team", tag: "Multiple clients, multi-location" },
  {
    value: "celebrity_wardrobe_manager",
    label: "Celebrity wardrobe manager",
    tag: "Single high-profile client",
  },
  {
    value: "archive_consultant",
    label: "Archive consultant",
    tag: "Long-term archive curation",
  },
  { value: "other", label: "Other", tag: "Tell us more" },
];

const HIGHLIGHTS = [
  {
    icon: BookMarked,
    title: "Catalog every piece.",
    body: "Brand, size, condition, photos — built for couture, not generic assets.",
  },
  {
    icon: MapPin,
    title: "Track every location.",
    body: "Studio rack, archive bin, on a client. Movement history included.",
  },
  {
    icon: Shirt,
    title: "Pull looks, fast.",
    body: "Build outfits and lookbooks the night before a shoot, not the week before.",
  },
];

export default function Onboarding() {
  const navigate = useNavigate();
  const { user, workspace, setUserPartial, setWorkspacePartial } = useAuth();

  const [step, setStep] = useState<1 | 2>(1);
  const [workspaceName, setWorkspaceName] = useState(
    workspace?.name && !workspace.name.endsWith("'s Workspace")
      ? workspace.name
      : ""
  );
  const [useCase, setUseCase] = useState<UseCase>("solo_stylist");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!user || !workspace) return null;

  async function onComplete(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const resp = await api.post<{ user: User; workspace: Workspace }>(
        "/onboarding/complete",
        {
          workspace_name:
            workspaceName.trim() || `${user!.full_name.split(" ")[0]} Studio`,
          primary_use_case: useCase,
        }
      );
      setUserPartial(resp.user);
      setWorkspacePartial(resp.workspace);
      setStep(2);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not save.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main
      data-testid="onboarding-page"
      className="min-h-screen bg-background text-foreground flex flex-col"
    >
      <header className="px-6 md:px-12 py-6 flex items-center justify-between border-b border-border">
        <span
          data-testid="onboarding-wordmark"
          className="font-display text-xl tracking-tight"
        >
          <Wordmark className="h-8" />
        </span>
        <div
          data-testid="onboarding-step-indicator"
          className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground"
        >
          Step {step} of 2
        </div>
      </header>

      <section className="flex-1 grid place-items-center px-6 py-10">
        <div className="w-full max-w-xl">
          {step === 1 && (
            <form
              data-testid="onboarding-step-1"
              onSubmit={onComplete}
              className="space-y-8"
              noValidate
            >
              <div>
                <p className="text-[11px] uppercase tracking-[0.28em] text-oxblood font-medium mb-4">
                  — Set up your workspace
                </p>
                <h1 className="font-display text-3xl sm:text-4xl leading-tight">
                  Hi {user.full_name.split(" ")[0]}. Let's name your studio.
                </h1>
                <p className="mt-3 text-sm md:text-base text-muted-foreground">
                  This appears on lookbooks, client-shared views, and reports.
                  You can change it later.
                </p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="workspace_name">Workspace name</Label>
                <Input
                  id="workspace_name"
                  data-testid="onboarding-workspace-name"
                  value={workspaceName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setWorkspaceName(e.target.value)
                  }
                  placeholder="e.g. Hart Styling Studio"
                  required
                />
              </div>

              <fieldset className="space-y-3">
                <legend className="text-sm font-medium mb-1">
                  What brings you to Wardrobe Index?
                </legend>
                {USE_CASES.map((uc) => (
                  <label
                    key={uc.value}
                    data-testid={`onboarding-usecase-${uc.value}`}
                    className={`flex items-start gap-3 cursor-pointer border px-4 py-3 transition-colors ${
                      useCase === uc.value
                        ? "border-oxblood bg-oxblood/5"
                        : "border-border hover:border-foreground/40"
                    }`}
                  >
                    <input
                      type="radio"
                      name="use_case"
                      value={uc.value}
                      checked={useCase === uc.value}
                      onChange={() => setUseCase(uc.value)}
                      className="mt-1.5 accent-oxblood"
                    />
                    <span>
                      <span className="block text-sm font-medium">
                        {uc.label}
                      </span>
                      <span className="block text-xs text-muted-foreground mt-0.5">
                        {uc.tag}
                      </span>
                    </span>
                  </label>
                ))}
              </fieldset>

              {error && (
                <p
                  role="alert"
                  data-testid="onboarding-error"
                  className="text-sm text-oxblood border border-oxblood/30 bg-oxblood/5 px-3 py-2"
                >
                  {error}
                </p>
              )}

              <Button
                type="submit"
                size="lg"
                data-testid="onboarding-step-1-submit"
                disabled={!workspaceName.trim() || submitting}
                className="w-full bg-oxblood hover:bg-oxblood-deep text-ivory rounded-none h-12"
              >
                {submitting ? "Saving…" : "Continue"}
              </Button>
            </form>
          )}

          {step === 2 && (
            <div data-testid="onboarding-step-2" className="space-y-8">
              <div>
                <p className="text-[11px] uppercase tracking-[0.28em] text-oxblood font-medium mb-4">
                  — You're in
                </p>
                <h1 className="font-display text-3xl sm:text-4xl leading-tight">
                  Welcome to Wardrobe Index, {user.full_name.split(" ")[0]}.
                </h1>
                <p className="mt-3 text-sm md:text-base text-muted-foreground">
                  You're on a <span className="font-medium text-foreground">14-day free trial</span>{" "}
                  of {workspace.plan === "trial" ? "Wardrobe Index" : `the ${workspace.plan} plan`}.
                  Here's what's waiting for you in the dashboard.
                </p>
              </div>

              <ul className="space-y-4 border border-border">
                {HIGHLIGHTS.map((h) => (
                  <li
                    key={h.title}
                    className="flex items-start gap-4 p-5 border-b border-border last:border-b-0"
                  >
                    <div className="w-10 h-10 grid place-items-center border border-oxblood/30 text-oxblood">
                      <h.icon className="w-5 h-5" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="font-display text-lg">{h.title}</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        {h.body}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>

              <Button
                size="lg"
                data-testid="onboarding-go-dashboard"
                onClick={() => navigate("/app", { replace: true })}
                className="w-full bg-oxblood hover:bg-oxblood-deep text-ivory rounded-none h-12 group"
              >
                Go to dashboard
                <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
              </Button>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
